// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <https://benrichardson.dev>
// Based on QuakeWatch by Ben Richardson — https://benrichardson.dev

export interface GlossaryEntry {
  term: string;
  def: string;
}

/** Domain glossary. Keys are lower-cased term slugs used by `data-term`. */
export const GLOSSARY: Record<string, GlossaryEntry> = {
  magnitude: {
    term: 'Magnitude',
    def: 'A measure of the size of an earthquake at its source, on a logarithmic scale. Each whole number up is about 32× more energy — a magnitude 6 releases ~1,000× the energy of a magnitude 4.',
  },
  moment_magnitude: {
    term: 'Moment magnitude (Mw)',
    def: 'The modern standard magnitude scale, based on the physical "seismic moment" (fault area × slip × rock rigidity). It works for the largest quakes where the old Richter scale saturates.',
  },
  local_magnitude: {
    term: 'Local magnitude (ML)',
    def: 'The original "Richter" scale, measured from the amplitude on a seismograph. Still used for small, nearby quakes. QuakeWatch shows whichever magnitude type USGS assigns to each event.',
  },
  depth: {
    term: 'Depth',
    def: 'How far below the surface the earthquake started (the hypocentre), in kilometres. Shallow quakes (<70 km) shake the surface hardest; deep quakes are felt less for the same magnitude.',
  },
  shallow: {
    term: 'Shallow / intermediate / deep',
    def: 'Depth bands used by seismologists: shallow is less than 70 km, intermediate 70–300 km, and deep more than 300 km. Most damaging quakes are shallow.',
  },
  epicentre: {
    term: 'Epicentre',
    def: 'The point on the Earth’s surface directly above where the earthquake started. The starting point below ground is the hypocentre or focus.',
  },
  gutenberg_richter: {
    term: 'Gutenberg–Richter law',
    def: 'The observation that small earthquakes are far more common than large ones, following a power law: for every magnitude 6 there are roughly ten magnitude 5s, a hundred magnitude 4s, and so on.',
  },
  b_value: {
    term: 'b-value',
    def: 'The slope of the Gutenberg–Richter line — how quickly earthquake frequency drops as magnitude rises. It sits near 1.0 for most of the world; lower values can hint at higher stress.',
  },
  completeness: {
    term: 'Magnitude of completeness',
    def: 'The smallest magnitude at which a seismic network reliably records every earthquake. Below it, many tiny quakes go undetected, which is why the frequency curve bends at the low end.',
  },
  energy: {
    term: 'Seismic energy',
    def: 'The energy radiated as seismic waves, estimated from magnitude. Because the scale is logarithmic, a single large earthquake can release more energy than thousands of small ones combined.',
  },
  pager: {
    term: 'PAGER alert',
    def: 'A USGS colour (green, yellow, orange, red) estimating likely human and economic impact within minutes of a quake, based on shaking, population and building vulnerability.',
  },
  mmi: {
    term: 'Shaking intensity (MMI)',
    def: 'The Modified Mercalli Intensity — how strongly the ground actually shook at a place, from I (not felt) to X+ (extreme). Unlike magnitude, it varies with distance and local geology.',
  },
  dyfi: {
    term: 'Did You Feel It? (felt reports)',
    def: 'The number of people who reported feeling a quake through the USGS "Did You Feel It?" survey — a crowd-sourced measure of how widely it was noticed.',
  },
  tsunami: {
    term: 'Tsunami flag',
    def: 'Marks events in oceanic regions where a tsunami could, in principle, be generated. It is not a tsunami warning — official warnings come from tsunami warning centres.',
  },
  significance: {
    term: 'Significance',
    def: 'A USGS score (higher = more notable) combining magnitude, felt reports and estimated impact. QuakeWatch uses it to surface the events most worth your attention.',
  },
  aftershock: {
    term: 'Aftershock',
    def: 'A smaller earthquake following a larger one (the mainshock) in the same area, as the crust re-adjusts. Sequences can last days to years and decay in number over time.',
  },
  swarm: {
    term: 'Earthquake swarm',
    def: 'A cluster of many earthquakes in one area over a short time with no single dominant mainshock — often linked to fluid or magma movement.',
  },
  tectonic_plate: {
    term: 'Tectonic plate',
    def: 'One of the large slabs of the Earth’s rigid outer shell. Most earthquakes happen at plate boundaries, where plates grind past, pull apart from, or dive beneath one another.',
  },
  ring_of_fire: {
    term: 'Ring of Fire',
    def: 'The horseshoe of boundaries around the Pacific Ocean where plates collide. It hosts about 90% of the world’s earthquakes and most of its large ones.',
  },
  quarry_blast: {
    term: 'Quarry blast / explosion',
    def: 'Human-made ground shaking (mining, demolition) that seismographs detect and USGS catalogues separately from natural earthquakes. QuakeWatch labels these so they aren’t mistaken for quakes.',
  },
};
